import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import { Chip, Grid, Typography, styled } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import StyledButton from '../Common/StyledButton';
import { therapyTypes } from '../../types/coach';
import { useAppSelector } from '../../redux/hooks';
import { RootState } from '../../redux/rootReducer';
import StyledBackButton from '../Common/StyledBackButton';
import { onboardingActions } from '../../redux/onboarding/onboardingSlice';
import { opacityKeyframe } from './CoachType';

const StyledChip = styled(Chip)({
  color: '#2A3C67',
  height: 46,
  borderRadius: 100,
  border: '1px solid #2A3C67',
});

const SelectedChip = styled(Chip)({
  color: '#FFF',
  height: 46,
  borderRadius: 100,
  border: '1px solid #2A3C67',
  backgroundColor: '#2A3C67',
  '&:hover': {
    backgroundColor: '#2A3C67',
  },
});

const slimScrollbarOnboardingStyles = {
  '&::-webkit-scrollbar': {
    width: '4px',
    backgroundColor: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    borderRadius: '3px',
    backgroundColor: '#7CAEBE',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: '#7CAEBE',
  },
  overFlowX: 'hidden',
};

export default function TherapyTypes({
  handleNext,
  handleBack,
}: {
  handleNext: () => void;
  handleBack: () => void;
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const therapist = useAppSelector((state: RootState) => state.onboarding.therapist);
  const [options, setOptions] = useState([]);

  useEffect(() => {
    const items = therapyTypes.filter((i) => therapist.coachTypes.includes(i.type));
    let values;
    items.forEach((i) => {
      values = values ? values.concat(i.values) : i.values;
    });
    setOptions(values);
  }, []);

  const validationSchema = Yup.object().shape({
    therapyTypes: Yup.array()
      .of(Yup.string().required(t('therapistOnboarding.validations.description')))
      .required(),
  });

  const formik = useFormik({
    initialValues: { therapyTypes: therapist.therapyTypes },
    validationSchema,
    onSubmit: () => {
      handleNext();
    },
    enableReinitialize: true,
  });

  const handleClick = (value: string) => {
    const index = formik.values.therapyTypes.findIndex((v) => v === value);
    let newValue;
    if (index >= 0) {
      const arr = [...formik.values.therapyTypes];
      arr.splice(index, 1);
      newValue = arr;
    } else {
      newValue = [...formik.values.therapyTypes, value];
    }
    dispatch(onboardingActions.setTherapyTypes(newValue));
  };

  return (
    <Box mb={4}>
      <form onSubmit={formik.handleSubmit} noValidate>
        <Grid container flexDirection="column" gap={8}>
          <Grid item textAlign="center">
            <Typography
              variant="title1Bold"
              color="primary.main"
              sx={{
                fontSize: { xs: '20px', md: '28px' },
                animation: `${opacityKeyframe} 0.5s ease forwards`,
              }}
            >
              {t('therapistOnboarding.help.title')}
            </Typography>
          </Grid>
          <Grid
            item
            container
            spacing={1}
            justifyContent="center"
            maxHeight={350}
            sx={{ overflowY: 'auto', ...slimScrollbarOnboardingStyles }}
            p={1}
          >
            {options?.map((option) => (
              <Grid item key={option}>
                {formik.values.therapyTypes.findIndex((v) => v === option) >= 0 ? (
                  <SelectedChip
                    label={t(option)}
                    size="medium"
                    variant="filled"
                    clickable
                    onClick={() => handleClick(option)}
                    sx={{ fontSize: { xs: '15px', md: '17px' }, paddingX: { xs: 2, md: 4 } }}
                  />
                ) : (
                  <StyledChip
                    label={t(option)}
                    size="medium"
                    variant="outlined"
                    clickable
                    onClick={() => handleClick(option)}
                    sx={{ fontSize: { xs: '15px', md: '17px' }, paddingX: { xs: 2, md: 4 } }}
                  />
                )}
              </Grid>
            ))}
          </Grid>

          <Grid item container justifyContent="space-between" pt={6}>
            <StyledBackButton onClick={handleBack} sx={{ padding: '12px 32px !important' }}>
              {' '}
              {t('therapistOnboarding.back')}
            </StyledBackButton>
            <StyledButton disabled={formik.values.therapyTypes.length === 0} paddingX={32}>
              {t('therapistOnboarding.next')}
            </StyledButton>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
}
